import { z } from 'zod';
import { TipoPartner } from './enums';
import {
  auditFieldsSchema,
  codiceAtecoSchema,
  codiceFiscaleSchema,
  dataOraSchema,
  emailSchema,
  idSchema,
  indirizzoSchema,
  paginationQuerySchema,
  partitaIvaSchema,
  telefonoSchema,
} from '../common';

/**
 * Il partner-azienda è un CONTENITORE: nessun collegamento operativo si
 * attacca all'azienda, ma sempre a una sua persona. Anche il professionista
 * singolo si registra come azienda + una persona dentro l'azienda.
 */

export const partnerCreateSchema = z.object({
  ragioneSociale: z.string().trim().min(1).max(255),
  tipo: TipoPartner.default('Azienda'),
  codiceFiscale: codiceFiscaleSchema,
  partitaIva: partitaIvaSchema.optional(),
  codiceAteco: codiceAtecoSchema.optional(),
  sedeLegale: indirizzoSchema,
  sedeOperativa: indirizzoSchema.optional(),
  email: emailSchema.optional(),
  pec: emailSchema.optional(),
  telefono: telefonoSchema.optional(),
});
export type PartnerCreate = z.infer<typeof partnerCreateSchema>;

export const partnerUpdateSchema = partnerCreateSchema.omit({ codiceFiscale: true }).partial();
export type PartnerUpdate = z.infer<typeof partnerUpdateSchema>;

export const partnerSchema = partnerCreateSchema.extend({
  id: idSchema,
  ...auditFieldsSchema.shape,
});
export type Partner = z.infer<typeof partnerSchema>;

/**
 * Riga della tabella partner: i pochi campi che servono a scansionare l'elenco
 * (stesso taglio di `clienteListItemSchema`).
 */
export const partnerListItemSchema = z.object({
  id: idSchema,
  ragioneSociale: z.string(),
  tipo: TipoPartner,
  codiceFiscale: z.string(),
  partitaIva: z.string().nullable(),
  comune: z.string().nullable(),
  provincia: z.string().nullable(),
  nPersone: z.number().int(),
  creatoIl: dataOraSchema,
});
export type PartnerListItem = z.infer<typeof partnerListItemSchema>;

export const partnerListQuerySchema = paginationQuerySchema.extend({
  tipo: TipoPartner.optional(),
});
export type PartnerListQuery = z.infer<typeof partnerListQuerySchema>;

export const partnerPersonaCreateSchema = z.object({
  nome: z.string().trim().min(1).max(80),
  cognome: z.string().trim().min(1).max(80),
  ruolo: z.string().trim().max(120).optional(),
  email: emailSchema.optional(),
  telefono: telefonoSchema.optional(),
});
export type PartnerPersonaCreate = z.infer<typeof partnerPersonaCreateSchema>;

export const partnerPersonaSchema = partnerPersonaCreateSchema.extend({
  id: idSchema,
  partnerId: idSchema,
  ...auditFieldsSchema.shape,
});
export type PartnerPersona = z.infer<typeof partnerPersonaSchema>;

export const partnerPersonaUpdateSchema = partnerPersonaCreateSchema.partial();
export type PartnerPersonaUpdate = z.infer<typeof partnerPersonaUpdateSchema>;

/**
 * Vista compatta di una persona-partner con la ragione sociale del contenitore,
 * per il picker del segnalatore sui clienti Esterno: il segnalatore
 * si sceglie SEMPRE come persona, mai come azienda in astratto.
 */
export const partnerPersonaPickerSchema = z.object({
  id: idSchema,
  nome: z.string(),
  cognome: z.string(),
  ruolo: z.string().nullable(),
  partnerId: idSchema,
  partnerRagioneSociale: z.string(),
});
export type PartnerPersonaPicker = z.infer<typeof partnerPersonaPickerSchema>;

/**
 * Query del picker: `q` cerca su nome/cognome persona e ragione sociale del
 * partner; `id` risolve una singola persona nota (serve a mostrare l'etichetta
 * del segnalatore già impostato su un cliente, senza dover ricaricare tutto
 * l'elenco). Senza filtri, i primi risultati alfabetici (lista può crescere).
 */
export const partnerPersonaPickerQuerySchema = z.object({
  q: z.string().trim().min(1).optional(),
  id: idSchema.optional(),
});
export type PartnerPersonaPickerQuery = z.infer<typeof partnerPersonaPickerQuerySchema>;

/**
 * Scorciatoia per il partner professionista (una persona sola, come un
 * commercialista): crea azienda-contenitore e la sua unica persona in un colpo solo.
 */
export const partnerProfessionistaCreateSchema = z.object({
  partner: partnerCreateSchema.extend({ tipo: z.literal('Professionista') }),
  persona: partnerPersonaCreateSchema,
});
export type PartnerProfessionistaCreate = z.infer<typeof partnerProfessionistaCreateSchema>;
